import type { User as FirebaseUser } from 'firebase/auth';
import { AI_BASE_URL } from '@/lib/ai-base-url';
import { isGpcEnabled } from '@/lib/analytics/openai-ads';
import {
  denyBrowserMeasurement,
  hasPendingBrowserOptOut,
  parsePendingOptOut,
  PENDING_OPT_OUT_PREFIX,
  type OptOutSource,
} from '@/lib/privacy-opt-out-state';

export type { OptOutSource } from '@/lib/privacy-opt-out-state';

/**
 * RETRY BUDGET — sign-in stays fail-closed (founder 2026-10-05, board
 * 0Z4YZENKphyXEXvSl7nf): a requested opt-out must be acknowledged before the
 * session is published. So a transient failure must be RETRIED, not turned
 * into a sign-out, but the retries must end while the person is still looking
 * at a spinner.
 *
 * - Each attempt (token + request + body) is capped at OPT_OUT_ATTEMPT_MS.
 * - Waits between attempts: 1s, 2s, 4s, 6s, so a fast-failing outage is
 *   probed at t = 0, 1, 3, 7 and 13 s.
 * - At most OPT_OUT_MAX_ATTEMPTS attempts and never past
 *   OPT_OUT_RETRY_BUDGET_MS of wall clock; the final attempt's timeout is
 *   clipped to what remains.
 *
 * Why 15 s: a payment-container recreate leaves /pay/* unreachable for a few
 * seconds (node runs as PID 1 and ignores SIGTERM, so the old container keeps
 * serving through docker's 10 s grace, then the new one boots in ~2-5 s). A
 * crash restart is similar. 15 s rides that out with margin, and five attempts
 * equal the server's per-account limit of 5/min, so the retries alone can
 * never trip it.
 *
 * Retried: network errors, timeouts, any 5xx, a 2xx without
 * `opted_out: true`, and 429 (waiting Retry-After when it fits in the budget).
 * Not retried: 400/401/403/404 and other 4xx, which a retry cannot change.
 */
export const OPT_OUT_ATTEMPT_MS = 3000;
export const OPT_OUT_RETRY_BUDGET_MS = 15000;
export const OPT_OUT_MAX_ATTEMPTS = 5;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 6000;

/** Wait after failed attempt `attempt` (0-based): 1, 2, 4, 6, 6 s. Shared with payment-api.ts. */
export function optOutBackoffMs(attempt: number): number {
  return Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
}

/**
 * Mirrors `ADS_OPT_OUT_UNCONFIRMED_CODE` in
 * services/payment/src/lib/ads-opt-out-gate.ts: a purchase, trial or
 * guest-checkout request carrying `gpc: true` whose opt-out the server could not
 * confirm. The server sends it only when it created nothing, which is what makes
 * re-sending the same request safe. Duplicated rather than imported (a server
 * module must not enter the browser bundle); privacy-opt-out-purchase.test.ts
 * reads the server file and pins the value.
 */
export const ADS_OPT_OUT_UNCONFIRMED_CODE = 'ads_opt_out_unconfirmed';

/**
 * Must a purchase, trial or guest-checkout request carry `gpc: true`?
 *
 * WHY (board qMSWo1t7k8otBnttEw9n, founder 2026-10-05: a GPC user is never
 * reported). Sign-in records the opt-out, but a RESTORED session never does
 * (auth.tsx: it only resumes a pending one), and a guest has no session at all.
 * Their conversions are reported from the server after the purchase, so the
 * purchase request itself has to carry the signal.
 *
 * - The browser sends GPC now → yes.
 * - An EXPLICIT opt-out is still pending for this account → yes. A pending
 *   GPC-derived marker while GPC is now off is stale and adds nothing, exactly
 *   as the recorder treats it on resume. A guest (no uid) has no marker of its
 *   own; another account's marker on this browser is not this purchase's.
 * - Admin impersonation ("view as user") → no. The signal belongs to the
 *   admin's browser, not the account, the same rule the restore listener
 *   follows. If the token claims cannot be read, the signal is honoured.
 *
 * The cheap checks run first, so a request with nothing to send never touches
 * the auth session.
 */
export async function purchaseOptOutRequested(session: {
  uid: () => string | null;
  isImpersonation: () => Promise<boolean>;
}): Promise<boolean> {
  const gpc = isGpcEnabled();
  if (!gpc && !hasPendingBrowserOptOut()) return false;
  const uid = session.uid();
  if (!gpc) {
    if (uid === null) return false;
    let stored: OptOutSource | null = null;
    try { stored = parsePendingOptOut(localStorage.getItem(PENDING_OPT_OUT_PREFIX + uid)); } catch { /* unreadable: nothing pending */ }
    if (stored !== 'explicit') return false;
  }
  if (uid !== null) {
    try {
      if (await session.isImpersonation()) return false;
    } catch { /* claims unreadable: honour the privacy signal */ }
  }
  return true;
}
/**
 * Longest a tab waits for ANOTHER tab's save (the cross-tab Web Lock below)
 * before saving on its own. A live holder always finishes inside its own
 * OPT_OUT_RETRY_BUDGET_MS, so the extra second only absorbs timer jitter; a
 * holder that does not finish by then is suspended or frozen (a backgrounded
 * mobile tab), and waiting longer would lock this tab's sign-in out.
 */
export const OPT_OUT_LOCK_WAIT_MS = OPT_OUT_RETRY_BUDGET_MS + 1000;
/** Upper bound on one confirmation: the lock wait plus this tab's own retry budget. */
export const OPT_OUT_MAX_CONFIRM_MS = OPT_OUT_LOCK_WAIT_MS + OPT_OUT_RETRY_BUDGET_MS;

export const OPT_OUT_ERROR = 'We could not save your privacy preference. Please try signing in again.';
export const OPT_OUT_RATE_LIMITED_ERROR =
  'We could not save your privacy preference. Please wait a minute, then try signing in again.';

type Account = Pick<FirebaseUser, 'uid' | 'getIdToken'>;
class TerminalOptOutError extends Error {}
class RetryableOptOutError extends Error {
  constructor(message: string, readonly retryAfterMs?: number) { super(message); }
}

export interface OptOutRecorderOptions {
  /** Is the browser sending GPC right now? Decides whether a pending GPC request is stale. */
  gpcSignal?: () => boolean;
  /** Test seam for the waits between attempts. */
  sleep?: (ms: number) => Promise<void>;
}

const realSleep = (ms: number) => new Promise<void>(resolve => { setTimeout(resolve, ms); });

type LockLike = {
  request: (name: string, options: { signal?: AbortSignal }, callback: () => Promise<unknown>) => Promise<unknown>;
};

/**
 * ONE retrier per account across tabs. The recorder lives in each tab's
 * AuthProvider, and a GPC sign-in in tab A makes tab B's listener resume the
 * same pending marker. Without coordination both tabs ran their own retry
 * loops, split the server's 5/min per-account allowance during an outage, and
 * the first tab to give up signed every tab out mid-retry. A Web Lock per uid
 * makes the second tab wait for the first; it then finds the marker cleared
 * and publishes without a request.
 *
 * THE WAIT IS BOUNDED (code-reviewer, board 0Z4YZENKphyXEXvSl7nf). The holder's
 * save is bounded only while the holder's timers run. A holder tab the browser
 * suspends mid-save never releases, and an unbounded wait turned that into a
 * new lockout: "already in progress" forever, with every reload queueing
 * behind it. So the request is withdrawn (AbortSignal) after
 * OPT_OUT_LOCK_WAIT_MS and this tab saves uncoordinated, which is exactly the
 * behaviour without Web Locks. Our own timer decides, not the signal alone: if
 * a browser ignored the signal and granted the lock late, the callback sees
 * that we already moved on and releases at once instead of saving twice.
 *
 * Also uncoordinated: Web Locks missing (older WebViews) or refused (opaque
 * origins). A save that RAN under the lock and failed is never run again.
 */
function withAccountLock<T>(uid: string, run: () => Promise<T>, waitMs = OPT_OUT_LOCK_WAIT_MS): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : (navigator as unknown as { locks?: LockLike }).locks;
  if (!locks || typeof locks.request !== 'function') return run();
  return new Promise<T>((resolve, reject) => {
    let state: 'waiting' | 'granted' | 'uncoordinated' = 'waiting';
    const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const saveUncoordinated = () => {
      if (state !== 'waiting') return;
      state = 'uncoordinated';
      if (timer !== undefined) clearTimeout(timer);
      controller?.abort();
      run().then(resolve, reject);
    };
    timer = setTimeout(saveUncoordinated, waitMs);
    try {
      locks.request(`jds.ads-opt-out.${uid}`, controller ? { signal: controller.signal } : {}, () => {
        // Granted after we stopped waiting: release immediately, do not save twice.
        if (state !== 'waiting') return Promise.resolve();
        state = 'granted';
        if (timer !== undefined) clearTimeout(timer);
        return run().then(resolve, reject);
      }).catch(saveUncoordinated); // refused or withdrawn before it was granted
    } catch {
      saveUncoordinated();
    }
  });
}

/** Retry-After as seconds or an HTTP date; the JSON body's retry_after_seconds when the header is not exposed cross-origin. */
function retryAfterMs(response: Response, data: unknown): number | undefined {
  const header = response.headers?.get?.('Retry-After');
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
    const date = Date.parse(header);
    if (Number.isFinite(date)) return Math.max(0, date - Date.now());
  }
  if (data && typeof data === 'object' && 'retry_after_seconds' in data) {
    const seconds = Number((data as { retry_after_seconds: unknown }).retry_after_seconds);
    if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  }
  return undefined;
}

/** One recorder per AuthProvider. Never send an opt-in or retry account creation. */
export function createOptOutRecorder(
  currentAccount: () => Account | null,
  options: OptOutRecorderOptions = {},
) {
  const gpcSignal = options.gpcSignal ?? isGpcEnabled;
  const sleep = options.sleep ?? realSleep;
  // In-memory copy of the marker, so blocked storage still protects this provider's lifetime.
  const pending = new Map<string, OptOutSource>();
  const confirmed = new Set<string>();
  const inFlight = new Map<string, Promise<void>>();

  function storedSource(uid: string): OptOutSource | null {
    try { return parsePendingOptOut(localStorage.getItem(PENDING_OPT_OUT_PREFIX + uid)); } catch { return null; }
  }

  function pendingSource(uid: string): OptOutSource | null {
    const stored = storedSource(uid);
    const inMemory = pending.get(uid) ?? null;
    // An explicit request anywhere wins; it is never downgraded to GPC.
    if (stored === 'explicit' || inMemory === 'explicit') return 'explicit';
    return stored ?? inMemory;
  }

  /** Returns whether the marker reached shared storage (so other tabs can see it). */
  function remember(uid: string, source: OptOutSource): boolean {
    pending.set(uid, source);
    try { localStorage.setItem(PENDING_OPT_OUT_PREFIX + uid, source); return true; } catch { return false; /* memory still protects this session */ }
  }

  /** True only when shared storage is readable and holds no pending marker for uid. */
  function markerCleared(uid: string): boolean {
    try { return parsePendingOptOut(localStorage.getItem(PENDING_OPT_OUT_PREFIX + uid)) === null; } catch { return false; }
  }

  function forget(uid: string) {
    pending.delete(uid);
    try { localStorage.removeItem(PENDING_OPT_OUT_PREFIX + uid); } catch { /* a later retry is harmless */ }
  }

  /**
   * Make sure `account`'s requested opt-out is saved server-side.
   *
   * @param requested The opt-out THIS call asks for (`gpc` at an explicit
   *   sign-in whose browser sent GPC), or `null` to only resume one already
   *   pending for this uid (session restore, impersonation).
   */
  return function ensureOptOut(account: Account, requested: OptOutSource | null): Promise<void> {
    const uid = account.uid;
    if (confirmed.has(uid)) return Promise.resolve();
    const existing = inFlight.get(uid);
    if (existing) {
      // The in-flight write saves the same preference. Record an explicit
      // request first, so a failure leaves it pending as explicit.
      if (requested === 'explicit') remember(uid, 'explicit');
      return existing;
    }

    const prior = pendingSource(uid);
    // A pending GPC-derived request whose signal has since been switched off
    // is stale: GPC is a live browser signal and nothing has been saved for it
    // yet. Drop the marker instead of forcing a save the person no longer
    // signals. This never opts anyone in (the server value is sticky), and an
    // EXPLICIT pending request is never dropped here.
    if (requested === null && prior === 'gpc' && !gpcSignal()) {
      forget(uid);
      return Promise.resolve();
    }
    const source: OptOutSource | null =
      requested === 'explicit' || prior === 'explicit' ? 'explicit' : requested ?? prior;
    if (!source) return Promise.resolve();
    // Written synchronously, before any await, so another tab sees it at once.
    const shared = remember(uid, source);
    denyBrowserMeasurement();

    const attemptOnce = async (timeoutMs: number) => {
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        // Race the WHOLE operation, including token retrieval and response
        // parsing. Aborting fetch alone cannot bound a stuck Firebase token.
        const request = async () => {
          const token = await account.getIdToken();
          if (controller.signal.aborted || currentAccount()?.uid !== uid) throw new TerminalOptOutError(OPT_OUT_ERROR);
          const base = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;
          const response = await fetch(`${base}/pay/attribution`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ user_id: uid, gpc: true }),
            signal: controller.signal,
          });
          if (response.status === 429) {
            let data: unknown = null;
            try { data = await response.json(); } catch { /* the header may still carry Retry-After */ }
            throw new RetryableOptOutError(OPT_OUT_RATE_LIMITED_ERROR, retryAfterMs(response, data));
          }
          if (response.status >= 400 && response.status < 500) throw new TerminalOptOutError(OPT_OUT_ERROR);
          if (!response.ok) throw new RetryableOptOutError(OPT_OUT_ERROR);
          const data: unknown = await response.json();
          if (!data || typeof data !== 'object' || !('opted_out' in data) || data.opted_out !== true) {
            throw new RetryableOptOutError(OPT_OUT_ERROR);
          }
        };
        await Promise.race([
          request(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => {
              controller.abort();
              reject(new RetryableOptOutError(OPT_OUT_ERROR));
            }, timeoutMs);
          }),
        ]);
      } finally {
        controller.abort();
        if (timer !== undefined) clearTimeout(timer);
      }
    };

    const save = async () => {
      const deadline = Date.now() + OPT_OUT_RETRY_BUDGET_MS;
      let lastMessage = OPT_OUT_ERROR;
      for (let attempt = 0; attempt < OPT_OUT_MAX_ATTEMPTS; attempt++) {
        if (currentAccount()?.uid !== uid) throw new Error(OPT_OUT_ERROR);
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;
        try {
          await attemptOnce(Math.min(OPT_OUT_ATTEMPT_MS, remaining));
          confirmed.add(uid);
          forget(uid);
          return;
        } catch (error) {
          if (error instanceof TerminalOptOutError) throw new Error(error.message);
          // Only the idempotent GPC-only write is retried. No click data,
          // account creation, login analytics, or conversion events are replayed.
          const retryable = error instanceof RetryableOptOutError ? error : new RetryableOptOutError(OPT_OUT_ERROR);
          lastMessage = retryable.message;
          if (attempt + 1 >= OPT_OUT_MAX_ATTEMPTS) break;
          const backoff = optOutBackoffMs(attempt);
          const wait = Math.max(backoff, retryable.retryAfterMs ?? 0);
          // A wait that would end past the budget cannot succeed in time:
          // fail now with the actionable message, keeping the request pending.
          if (Date.now() + wait >= deadline) break;
          await sleep(wait);
        }
      }
      throw new Error(lastMessage);
    };
    const run = async () => {
      // While this tab waited for the lock, another tab may have confirmed
      // (or dropped as stale) the same account's marker. A resume has nothing
      // left to do. A fresh request (`requested` set) still sends its own,
      // idempotent write rather than infer success from a missing key.
      if (requested === null && shared && markerCleared(uid)) {
        pending.delete(uid);
        return;
      }
      await save();
    };
    const result = withAccountLock(uid, run).finally(() => { inFlight.delete(uid); });
    inFlight.set(uid, result);
    return result;
  };
}
